import React, { useState } from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { Vertebra, VertebraTemplate } from '../../types';
import VertebraConfigModal from './VertebraConfigModal';

interface Props {
  vertebrae: Vertebra[];
  templates: VertebraTemplate[];
  onRemove: (id: string) => void;
  onToggle: (id: string) => void;
  onUpdateConfig: (id: string, config: Record<string, any>) => void;
}

function SpineSection({ vertebrae, templates, onRemove, onToggle, onUpdateConfig }: Props) {
  const [configModalVertebra, setConfigModalVertebra] = useState<Vertebra | null>(null);

  function getCategory(type: string): string {
    const template = templates.find(t => t.type === type);
    return template?.category || 'filter';
  }

  // Decide whether a vertebra is fully set up. Anything that doesn't need API
  // setup (free checks, dedup, format transforms) is always Ready. Anything
  // that does need API setup checks for type-specific config fields, since
  // different vertebra types use different config keys (e.g. EO uses
  // eoApiToken + eoListId, custom_api uses apiUrl).
  function isVertebraConfigured(vertebra: Vertebra): boolean {
    const template = templates.find(t => t.type === vertebra.type);
    if (!template?.requiresApiSetup) {return true;}
    const cfg = vertebra.config || {};
    switch (vertebra.type) {
      case 'email_oversight_validation':
        return !!cfg.eoApiToken && cfg.eoListId !== undefined && cfg.eoListId !== null && cfg.eoListId !== '';
      case 'custom_api':
        return !!cfg.apiUrl;
      default:
        // Conservative fallback for any new API-backed vertebra type that
        // hasn't been added here yet — assume an apiUrl is the trigger.
        return !!cfg.apiUrl;
    }
  }

  function getTemplateEmoji(type: string): string {
    const map: Record<string, string> = {
      email_validation: '\u2709',
      email_oversight_validation: '\uD83D\uDEE1',
      free_email_check: '\uD83C\uDD93',
      phone_validation: '\u260E',
      free_phone_check: '\uD83C\uDD93',
      address_validation: '\uD83D\uDCCD',
      data_enrichment: '\uD83D\uDCCA',
      database_lookup: '\uD83D\uDD0D',
      custom_api: '\uD83D\uDD0C',
      filter_rule: '\uD83D\uDCCB',
      dedup_check: '\uD83D\uDDC2',
      format_transform: '\u2728',
    };
    return map[type] || '\u2699';
  }

  return (
    <div className="spine-section">
      <div className="spine-title">
        The Spine (Vertebrae)
      </div>

      <Droppable droppableId="spine">
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`spine-drop-zone ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
          >
            {vertebrae.length === 0 ? (
              <div className="spine-empty">
                <p style={{ fontSize: 16, fontWeight: 500, marginBottom: 4 }}>No vertebrae yet</p>
                <p>Drag vertebrae from the sidebar to start building your spine</p>
              </div>
            ) : (
              vertebrae.map((vertebra, index) => (
                <Draggable key={vertebra.id} draggableId={vertebra.id} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`vertebra-card ${!vertebra.isActive ? 'inactive' : ''}`}
                      style={{
                        ...provided.draggableProps.style,
                        boxShadow: snapshot.isDragging ? '0 8px 24px rgba(0,0,0,0.15)' : undefined,
                      }}
                    >
                      <div
                        className="vertebra-drag-handle"
                        {...provided.dragHandleProps}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <circle cx="9" cy="6" r="1.5"/><circle cx="15" cy="6" r="1.5"/>
                          <circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/>
                          <circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="18" r="1.5"/>
                        </svg>
                      </div>

                      <div className={`template-icon ${getCategory(vertebra.type)}`} style={{ width: 36, height: 36, fontSize: 18 }}>
                        {getTemplateEmoji(vertebra.type)}
                      </div>

                      <div className="vertebra-info" onClick={() => setConfigModalVertebra(vertebra)}>
                        <div className="vertebra-name">{vertebra.name}</div>
                        <div className="vertebra-type">
                          {vertebra.type.replace(/_/g, ' ')} | On fail: {vertebra.onFail}
                        </div>
                      </div>

                      <span className={`vertebra-status ${isVertebraConfigured(vertebra) ? 'configured' : 'needs-setup'}`}>
                        {isVertebraConfigured(vertebra) ? 'Ready' : 'Setup needed'}
                      </span>

                      <div className="vertebra-actions">
                        <button
                          className={`toggle ${vertebra.isActive ? 'active' : ''}`}
                          onClick={() => onToggle(vertebra.id)}
                          title={vertebra.isActive ? 'Disable' : 'Enable'}
                        />
                        <button
                          className="btn-icon"
                          onClick={() => {
                            if (window.confirm(`Permanently delete "${vertebra.name}"?\n\nPass/Fail counters will be lost. To temporarily remove this vertebra from the spine instead, use the toggle (it preserves the counters).`)) {
                              onRemove(vertebra.id);
                            }
                          }}
                          title="Remove"
                          style={{ color: '#ef4444' }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>

      {/* Config Modal */}
      {configModalVertebra && (
        <VertebraConfigModal
          vertebra={configModalVertebra}
          onSave={(config) => {
            onUpdateConfig(configModalVertebra.id, config);
            setConfigModalVertebra(null);
          }}
          onClose={() => setConfigModalVertebra(null)}
        />
      )}
    </div>
  );
}

export default SpineSection;
